import { supabase } from '@/lib/supabase';
import type { SiteSettings, PureHeartSettings, LiveStreamSettings, Donation, Page } from '@/lib/types';

export type TableName =
  | 'leaders'
  | 'sermons'
  | 'events'
  | 'news'
  | 'gallery_images'
  | 'testimonials'
  | 'ministries'
  | 'donations'
  | 'pages'
  | 'books'
  | 'media_library';

export async function fetchRows<T>(
  table: TableName,
  orderBy = 'created_at',
  ascending = false,
): Promise<T[]> {
  const { data, error } = await supabase
    .from(table)
    .select('*')
    .order(orderBy, { ascending });
  if (error) throw error;
  return (data as T[]) ?? [];
}

export async function createRow(
  table: TableName,
  payload: Record<string, unknown>,
): Promise<{ id: string }> {
  const { data, error } = await supabase.from(table).insert(payload).select('id').single();
  if (error) throw error;
  return data as { id: string };
}

export async function updateRow(
  table: TableName,
  id: string,
  payload: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase.from(table).update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteRow(table: TableName, id: string): Promise<void> {
  const { error } = await supabase.from(table).delete().eq('id', id);
  if (error) throw error;
}

export async function fetchSiteSettings(): Promise<SiteSettings> {
  const { data, error } = await supabase.from('site_settings').select('*').eq('id', 1).single();
  if (error) throw error;
  return data as SiteSettings;
}

export async function updateSiteSettings(
  payload: Partial<SiteSettings>,
): Promise<void> {
  const { error } = await supabase.from('site_settings').update(payload).eq('id', 1);
  if (error) throw error;
}

export async function fetchPureheartSettings(): Promise<PureHeartSettings> {
  const { data, error } = await supabase
    .from('pureheart_settings')
    .select('*')
    .eq('id', 1)
    .single();
  if (error) throw error;
  return data as PureHeartSettings;
}

export async function updatePureheartSettings(
  payload: Partial<PureHeartSettings>,
): Promise<void> {
  const { error } = await supabase.from('pureheart_settings').update(payload).eq('id', 1);
  if (error) throw error;
}

export async function fetchLiveStreamSettings(): Promise<LiveStreamSettings> {
  const { data, error } = await supabase
    .from('live_stream_settings')
    .select('*')
    .eq('id', 1)
    .single();
  if (error) throw error;
  return data as LiveStreamSettings;
}

export async function updateLiveStreamSettings(
  payload: Partial<LiveStreamSettings>,
): Promise<void> {
  const { error } = await supabase.from('live_stream_settings').update(payload).eq('id', 1);
  if (error) throw error;
}

export interface DashboardStats {
  leaders: number;
  sermons: number;
  events: number;
  news: number;
  gallery: number;
  testimonials: number;
  ministries: number;
  prayer_requests: number;
  contact_submissions: number;
  newsletter_subscribers: number;
}

export async function fetchDashboardStats(): Promise<DashboardStats> {
  const tables: Array<keyof DashboardStats> = [
    'leaders',
    'sermons',
    'events',
    'news',
    'gallery',
    'testimonials',
    'ministries',
    'prayer_requests',
    'contact_submissions',
    'newsletter_subscribers',
  ];

  const stats = {} as DashboardStats;

  await Promise.all(
    tables.map(async (table) => {
      const dbTable =
        table === 'gallery'
          ? 'gallery_images'
          : table === 'prayer_requests'
            ? 'prayer_requests'
            : table === 'contact_submissions'
              ? 'contact_submissions'
              : table === 'newsletter_subscribers'
                ? 'newsletter_subscribers'
                : table;
      const { count, error } = await supabase
        .from(dbTable)
        .select('*', { count: 'exact', head: true });
      stats[table] = error ? 0 : count ?? 0;
    }),
  );

  return stats;
}

export async function fetchSubmissions<T>(
  table: 'prayer_requests' | 'contact_submissions' | 'newsletter_subscribers',
): Promise<T[]> {
  const { data, error } = await supabase
    .from(table)
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as T[]) ?? [];
}

export async function updateSubmissionStatus(
  table: 'prayer_requests' | 'contact_submissions',
  id: string,
  status: string,
): Promise<void> {
  const { error } = await supabase.from(table).update({ status }).eq('id', id);
  if (error) throw error;
}

export async function deleteSubmission(
  table: 'prayer_requests' | 'contact_submissions' | 'newsletter_subscribers',
  id: string,
): Promise<void> {
  const { error } = await supabase.from(table).delete().eq('id', id);
  if (error) throw error;
}

export async function fetchDonations(): Promise<Donation[]> {
  const { data, error } = await supabase
    .from('donations')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as Donation[]) ?? [];
}

export async function updateDonationStatus(id: string, status: string): Promise<void> {
  const { error } = await supabase.from('donations').update({ status }).eq('id', id);
  if (error) throw error;
}

export async function deleteDonation(id: string): Promise<void> {
  const { error } = await supabase.from('donations').delete().eq('id', id);
  if (error) throw error;
}

export async function recordDonation(payload: {
  full_name: string;
  email: string;
  amount: string;
  payment_method: string;
}): Promise<void> {
  const { error } = await supabase.from('donations').insert(payload);
  if (error) throw error;
}

export async function sendDonationEmails(payload: {
  donorEmail: string;
  fullName: string;
  amount: string;
  paymentMethod: string;
}): Promise<void> {
  const { error } = await supabase.functions.invoke('notify-donation', {
    body: payload,
  });
  if (error) throw error;
}

export type AdminRole = 'super_admin' | 'admin' | 'editor';

export interface AdminUserRow {
  id: string;
  email: string;
  full_name: string;
  role: AdminRole;
  created_at: string;
}

export async function listAdminUsers(): Promise<AdminUserRow[]> {
  const { data, error } = await supabase.functions.invoke('manage-users', {
    body: { action: 'list' },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return (data?.data as AdminUserRow[]) ?? [];
}

export async function createAdminUser(payload: {
  email: string;
  password: string;
  fullName: string;
  role: AdminRole;
}): Promise<void> {
  const { data, error } = await supabase.functions.invoke('manage-users', {
    body: { action: 'create', ...payload },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
}

export async function updateAdminUserRole(
  id: string,
  role: AdminRole,
  fullName?: string,
): Promise<void> {
  const { data, error } = await supabase.functions.invoke('manage-users', {
    body: { action: 'updateRole', id, role, fullName },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
}

export async function deleteAdminUser(id: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('manage-users', {
    body: { action: 'delete', id },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
}

export async function sendTestEmail(payload: {
  to: string;
  subject: string;
  text: string;
  html?: string;
}): Promise<{ success: boolean; id?: string }> {
  const { data, error } = await supabase.functions.invoke('send-test-email', {
    body: payload,
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data as { success: boolean; id?: string };
}

export async function uploadImage(file: File, folder = 'gallery'): Promise<string> {
  const cleaned = file.name
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9._-]/g, '')
    .toLowerCase();
  const base = cleaned.includes('.')
    ? cleaned.slice(0, cleaned.lastIndexOf('.')).slice(0, 40) || 'image'
    : cleaned.slice(0, 40) || 'image';
  const ext = cleaned.includes('.')
    ? cleaned.slice(cleaned.lastIndexOf('.') + 1).slice(0, 5) || 'jpg'
    : 'jpg';
  const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${base}.${ext}`;

  const { error } = await supabase.storage
    .from('gallery')
    .upload(path, file, { cacheControl: '3600', upsert: false });
  if (error) throw error;

  const { data } = supabase.storage.from('gallery').getPublicUrl(path);
  return data.publicUrl;
}

export async function fetchPublishedPages(): Promise<Page[]> {
  const { data, error } = await supabase
    .from('pages')
    .select('*')
    .eq('is_published', true)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data as Page[]) ?? [];
}

export async function fetchPageBySlug(slug: string): Promise<Page | null> {
  const { data, error } = await supabase
    .from('pages')
    .select('*')
    .eq('slug', slug)
    .eq('is_published', true)
    .maybeSingle();
  if (error) throw error;
  return (data as Page) ?? null;
}